import { ColumnDetails, ColumnType } from 'apps/webcomponents/src/grid/types/common';
import { Filter } from 'apps/webcomponents/src/grid/gridFilters/types';
import { SelectOption } from 'apps/webcomponents/src/ui/gzDropdown/types';
import { GzEnumFilterWebComponent } from 'apps/webcomponents/src/ui/gzEnumFilter';
import { EnumListItem } from 'apps/webcomponents/src/ui/gzEnumList/types';
import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { htmlEncode } from '../../../utils/escape.utils';
import { MaintenanceWindowPatchManagementVendorProductSelectorGridDataProvider } from '../dataProvider/MaintenanceWindowPatchManagementVendorProductSelectorGridDataProvider';
import { Column, ColumnKeys, ProductRecord, VendorRecord, VendorProductsPair, PatchOsType } from '../types';
import { LinkType } from 'apps/webcomponents/src/ui/gzLinkComponent/types';

export class MaintenanceWindowPatchManagementVendorProductSelectorView extends BaseWebComponentView {
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');

    this.dom.$vendorSelector = this.shadow.querySelector('#vendor-selector');

    this.dom.$productSelector = this.shadow.querySelector('#product-selector');

    this.dom.$addButton = this.shadow.querySelector('#add-button');

    this.dom.$grid = this.shadow.querySelector('#vendor-products-pairs');

    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.dom.$vendorSelector.label = htmlEncode(this.dependencies.translateFn('Vendor'));
    this.dom.$vendorSelector.placeholder = htmlEncode(this.dependencies.translateFn('Select vendor'));
    this.dom.$vendorSelector.errorMessage = htmlEncode(this.dependencies.translateFn('Error loading vendors'));

    this.dom.$productSelector.label = htmlEncode(this.dependencies.translateFn('Products'));
    this.dom.$productSelector.searchPlaceholderString = htmlEncode(this.dependencies.translateFn('Search for product'));
    this.dom.$productSelector.confirmActionButtonString = htmlEncode(this.dependencies.translateFn('Select products'));
    this.dom.$productSelector.allItemsLabel = htmlEncode(this.dependencies.translateFn('All products'));

    this.dom.$addButton.innerHTML = htmlEncode(this.dependencies.translateFn('Add'));
  }

  getGridColumnDetails(): ColumnDetails<Column, ColumnKeys>[] {
    const columnDetails: ColumnDetails<Column, ColumnKeys>[] = [
      {
        column: Column.VENDOR,
        key: ColumnKeys.VENDOR,
        columnName: htmlEncode(this.dependencies.translateFn('Vendor')),
        type: ColumnType.STRING,
        removable: false,
        filteringDisabled: true,
      },
      {
        column: Column.PRODUCTS,
        key: ColumnKeys.PRODUCTS,
        columnName: htmlEncode(this.dependencies.translateFn('Products')),
        type: ColumnType.STRING,
        removable: false,
        filteringDisabled: true,
      },
      {
        column: Column.ACTIONS,
        key: ColumnKeys.ACTIONS,
        columnName: htmlEncode(this.dependencies.translateFn('Actions')),
        type: ColumnType.ACTION,
        removable: false,
        filteringDisabled: true,
      },
    ];

    return columnDetails;
  }

  getGridFilters(): Filter<ColumnKeys>[] {
    return [{}];
  }

  buildSuccessView(data: {
    gridDataProvider: MaintenanceWindowPatchManagementVendorProductSelectorGridDataProvider;
    disabled: boolean;
  }) {
    // Initialize gz-enum-filter
    this.dom.$productSelector.canSelectAll = true;

    const $grid = this.dom.$grid;

    // Initialize gz-grid-view
    $grid.propertiesSet = false;
    $grid.columns = Object.values(Column).filter(value => !isNaN(Number(value)));
    $grid.viewId = 'vendor-products-pairs-view';
    $grid.allColumnDetails = this.getGridColumnDetails();
    $grid.filters = this.getGridFilters();
    $grid.isCompactViewChecked = true;
    $grid.removeFilters = true;
    $grid.hideHeaderActions = true;
    $grid.hideColumnHeaders = false;
    $grid.hideFooter = true;

    $grid.columnRenderers = {
      [ColumnKeys.VENDOR]: this.renderVendorCell.bind(this),
      [ColumnKeys.PRODUCTS]: this.renderProductsCell.bind(this, data.disabled),
      [ColumnKeys.ACTIONS]: this.renderActionsCell.bind(this, data.disabled),
    };
    $grid.dataProvider = data.gridDataProvider;

    $grid.injectDependencies(this.dependencies);
    $grid.start();

    if (data.disabled) {
      // Disable form controls
      this.dom.$vendorSelector.setAttribute('disabled', 'disabled');
    }
  }

  renderVendorCell(rowData: { item: VendorProductsPair }, columnDetails: ColumnDetails<Column, ColumnKeys>): string {
    // No need for htmlEncode when returning string
    return this.getComputedVendorNameWithOS(rowData.item.vendor.name, rowData.item.os);
  }

  renderProductsCell(
    disabled: boolean,
    rowData: { item: VendorProductsPair },
    columnDetails: ColumnDetails<Column, ColumnKeys>
  ): DocumentFragment {
    const productNames = rowData.item.products.map(product => htmlEncode(product.name)).join(', ');

    const documentFragment = this.createTemplateElement(`<span>
      <gz-link href="#" type="${LinkType.UNDERLINE}"><span>
        ${productNames}
      </span></gz-link>
    </span>`).content;

    const $editLink = documentFragment.querySelector('gz-link');
    const $editContainer = documentFragment.querySelector('span');

    $editLink?.addEventListener('click', event => {
      event.preventDefault();

      if (disabled) {
        return;
      }

      const vendorId = rowData.item.vendor.id;

      // Create disabled gz-enum-filter
      const productSelectorDocumentFragment = this.createProductSelectorForRow();

      const $productSelector: any = productSelectorDocumentFragment.querySelector('gz-enum-filter');

      const doneCallback = () => {
        // Remove product selector
        $productSelector.remove();

        // Show link
        $editLink.removeAttribute('hidden');
      };

      // Remove product selector when it loses focus
      $productSelector.addEventListener('blur', () => {
        doneCallback();
      });

      // Fire custom event
      const showEvent = new CustomEvent('showVendorProductsPair', {
        detail: {
          vendorId,
          $productSelector,
          selectedProducts: rowData.item.products,
        },
      });
      this.dom.$grid.dispatchEvent(showEvent);

      $productSelector.addEventListener('selectionChange', (event: CustomEvent) => {
        // Fire custom event
        const editEvent = new CustomEvent('editVendorProductsPair', {
          detail: {
            vendorId,
            options: $productSelector.filterOptions,
            selectedOptions: $productSelector.enumList.getCheckedOptions(),
            selectedProducts: rowData.item.products,
          },
        });
        this.dom.$grid.dispatchEvent(editEvent);
      });

      // Hide link
      $editLink.setAttribute('hidden', 'hidden');

      // Add gz-enum-filter to page
      $editContainer?.appendChild(productSelectorDocumentFragment);
      $productSelector.focus();
    });
    this.fixIndexSelect('absolute', '');

    return documentFragment;
  }

  fixIndexSelect(position, transform) {
    const elems = this.dom.$grid.shadowRoot.querySelector('.grid').shadowRoot.querySelectorAll('tr');
    const length = elems.length;
    for (let index = 1; index < length; index++) {
      elems[index].style.position = position;
      elems[index].style.transform = transform;
    }
  }

  createProductSelectorForRow(): DocumentFragment {
    const documentFragment = this.createTemplateElement(`<gz-enum-filter style="position: absolute; top: 0;"
      id="product-selector"
      disabled="disabled"
      tabindex="0"
    ></gz-enum-filter>`).content;

    const $productSelector = documentFragment.querySelector('gz-enum-filter') as GzEnumFilterWebComponent;

    $productSelector.label = this.dependencies.translateFn('Products');
    $productSelector.searchPlaceholderString = this.dependencies.translateFn('Search for product');
    $productSelector.confirmActionButtonString = this.dependencies.translateFn('Select products');
    $productSelector.allItemsLabel = this.dependencies.translateFn('All products');

    $productSelector.canSelectAll = true;

    $productSelector.addEventListener('open', () => this.fixIndexSelect('relative', 'none'));
    $productSelector.addEventListener('close', () => this.fixIndexSelect('relative', 'none'));

    return documentFragment;
  }

  renderActionsCell(
    disabled: boolean,
    rowData: { item: VendorProductsPair },
    columnDetails: ColumnDetails<Column, ColumnKeys>
  ): DocumentFragment | null {
    const documentFragment = this.createTemplateElement(`<gz-icon-button
      icon="ds-icon-clear"
      buttoncolor="secondary"
      fontsize="20"
      buttontype="round"
      buttonfill="quiet"
      size="24"
    ></gz-icon-button>`).content;

    const $button = documentFragment.querySelector('gz-icon-button');

    if (disabled) {
      $button?.setAttribute('disabled', 'disabled');
    } else {
      $button?.addEventListener('click', () => {
        const vendorId = rowData.item.vendor.id;

        // Fire custom event
        const event = new CustomEvent('deleteVendorProductsPair', { detail: { vendorId } });
        this.dom.$grid.dispatchEvent(event);

        event.preventDefault();
      });
    }

    return documentFragment;
  }

  buildErrorView(error: Error) {
    console.error(error);
  }

  getVendorOptions(vendors: VendorRecord[]): SelectOption[] {
    return vendors
      .map(vendor => {
        return {
          title: this.getComputedVendorNameWithOS(vendor.name, vendor.os),
          value: vendor.vendorId.toString(),
          data: {
            os: vendor.os,
            originalVendorName: vendor.name,
          },
        };
      })
      .sort((a: SelectOption, b: SelectOption) => {
        const firstName = a.title.toLocaleLowerCase();
        const secondName = b.title.toLocaleLowerCase();

        if (firstName === secondName) {
          return 0;
        }

        return firstName > secondName ? 1 : -1;
      });
  }

  getProductOptions(
    products: ProductRecord[],
    isSelected: ((productId: string) => boolean) | null = null
  ): EnumListItem[] {
    return products
      .map(product => {
        return {
          label: product.name,
          value: product.productId,
          selected: isSelected ? isSelected(product.productId) : false,
        } as EnumListItem;
      })
      .sort((a: EnumListItem, b: EnumListItem) => {
        const firstName = a.label.toLocaleLowerCase();
        const secondName = b.label.toLocaleLowerCase();

        if (firstName === secondName) {
          return 0;
        }

        return firstName > secondName ? 1 : -1;
      });
  }

  private createTemplateElement(html: string): HTMLTemplateElement {
    const templateElement = document.createElement('template');
    templateElement.innerHTML = html;

    return templateElement;
  }

  private getComputedVendorNameWithOS(vendorName: string, vendorOS?: PatchOsType): string {
    switch (vendorOS) {
      case PatchOsType.WINDOWS:
        return vendorName + ' (' + this.dependencies.translateFn('Windows') + ')';
      case PatchOsType.LINUX:
        return vendorName + ' (' + this.dependencies.translateFn('Linux') + ')';
      case PatchOsType.MAC:
        return vendorName + ' (' + this.dependencies.translateFn('macOS') + ')';
      default:
        return vendorName;
    }
  }
}
